'use client'

import { formatGameMoney } from '@/lib/utils/formatGameMoney'

import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Loader2, Check, AlertCircle, CreditCard, Lock, X } from 'lucide-react'
import StripeElementsWrapper from '@/components/payment/StripeElements'
import { cn } from '@/lib/utils'
import { getPackageIconPath, getPackageTierLabel } from '@/lib/utils/ntTokenPackageIcons'

interface TokenPackage {
  id: string
  name: string
  base_tokens: number
  bonus_tokens: number
  total_tokens: number
  price_eur: number
  price_display: string
  value_proposition: string
  is_recommended: boolean
  savings_message: string
}

interface NTTopupPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  instanceId: string
  currentBalance: number
  onPurchaseComplete: (amount: number) => void
  selectedPackageId: string | null
  className?: string
}

type PaymentStep = 'payment_form' | 'processing' | 'success' | 'failed'

export function NTTopupPaymentModal({
  isOpen,
  onClose,
  instanceId,
  currentBalance,
  onPurchaseComplete,
  selectedPackageId,
  className
}: NTTopupPaymentModalProps) {
  const [packages, setPackages] = useState<TokenPackage[]>([])
  const [selectedPackage, setSelectedPackage] = useState<TokenPackage | null>(null)
  const [paymentStep, setPaymentStep] = useState<PaymentStep>('payment_form')

  const [clientSecret, setClientSecret] = useState<string>('')

  const [paymentResultPollIntervalHandler, setPaymentResultPollIntervalHandler] =
    useState<NodeJS.Timeout | null>(null)
  const [paymentResultPollCount, setPaymentResultPollCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')

  const resetState = useCallback(() => {
    setPackages([])
    setSelectedPackage(null)
    setPaymentStep('payment_form')
    setPaymentResultPollCount(0)
    setClientSecret('')
    setError('')
    setIsLoading(false)
  }, [])

  // Fetch packages when modal opens
  const fetchPackages = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await fetch(`/api/np-tokens/packages?instance_id=${instanceId}`, {})

      if (!response.ok) {
        console.error('[NTTopupPaymentModal] HTTP Error:', response.status, response.statusText)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success && data.packages) {
        setPackages(data.packages)
      } else {
        const errorMsg = data.error || 'Failed to load packages'
        console.error('[NTTopupPaymentModal] API error:', errorMsg)
        setError(errorMsg)
      }
    } catch (err) {
      console.error('[NTTopupPaymentModal] Error loading packages:', err)
      const errorMsg = err instanceof Error ? err.message : 'Network error loading packages'
      setError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }, [instanceId])

  useEffect(() => {
    if (isOpen && packages.length === 0) {
      fetchPackages()
    }
  }, [isOpen, packages.length, fetchPackages])

  const handlePackageSelect = useCallback(
    async (pkg: TokenPackage) => {
      setSelectedPackage(pkg)
      setError('')
      setClientSecret('')
      setPaymentStep('payment_form')
      setIsLoading(true)

      try {
        const requestBody = {
          instance_id: instanceId,
          package_id: pkg.id
        }

        const response = await fetch('/api/np-tokens/topup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        })

        if (!response.ok) {
          console.error('[NTTopupPaymentModal] HTTP Error:', response.status, response.statusText)
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data: {
          success: boolean
          client_secret: string
          payment_intent_id: string
          purchase_details: Record<string, any>
          error?: string
        } = await response.json()

        if (data.success && data.client_secret && data.payment_intent_id) {
          setClientSecret(data.client_secret)
        } else {
          const errorMsg = data.error || 'Failed to create payment intent'
          console.error('[NTTopupPaymentModal] API error:', errorMsg)
          setError(errorMsg)
          setPaymentStep('failed')
        }
      } catch (err) {
        console.error('[NTTopupPaymentModal] Error creating payment:', err)
        const errorMsg = err instanceof Error ? err.message : 'Network error creating payment'
        setError(errorMsg)
        setPaymentStep('failed')
      } finally {
        setIsLoading(false)
      }
    },
    [instanceId]
  )

  // Reset modal state whenever it closes so re-opening starts fresh
  useEffect(() => {
    if (!isOpen) {
      resetState()
    }
  }, [instanceId, isOpen, resetState])

  // Automatically transition to payment form if a package is pre-selected
  useEffect(() => {
    if (!isOpen) return
    if (!selectedPackageId) return
    if (!packages.length) return
    if (isLoading) return

    // Avoid re-selecting if already set
    if (selectedPackage && selectedPackage.id === selectedPackageId) {
      return
    }

    const matchedPackage = packages.find((pkg) => pkg.id === selectedPackageId)
    if (matchedPackage) {
      handlePackageSelect(matchedPackage)
    }
  }, [isOpen, selectedPackageId, packages, selectedPackage, isLoading, handlePackageSelect])

  // Handles the interval's handler cancellation
  useEffect(() => {
    // Handles closed modal check
    if (!isOpen && paymentResultPollIntervalHandler) {
      clearInterval(paymentResultPollIntervalHandler)
      setPaymentResultPollIntervalHandler(null)
      return
    }

    // Cancel after 10 tries
    if (
      paymentStep === 'processing' &&
      paymentResultPollIntervalHandler &&
      paymentResultPollCount > 10
    ) {
      clearInterval(paymentResultPollIntervalHandler)
      setPaymentResultPollIntervalHandler(null)
      console.error('[NTTopupPaymentModal] Payment status poll timeout')
      setError('Payment status poll timeout')
      setPaymentStep('failed')
      return
    }

    // Cancel once state changes.
    if (
      (paymentStep === 'success' || paymentStep === 'failed') &&
      paymentResultPollIntervalHandler
    ) {
      clearInterval(paymentResultPollIntervalHandler)
      setPaymentResultPollIntervalHandler(null)
      return
    }
  }, [isOpen, paymentStep, paymentResultPollCount, paymentResultPollIntervalHandler])

  // Handles interval clearing when unmounting this component
  useEffect(() => {
    return () => {
      if (paymentResultPollIntervalHandler) {
        clearInterval(paymentResultPollIntervalHandler)
      }
    }
  }, [paymentResultPollIntervalHandler])

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    setPaymentStep('processing')

    // Constantly poll for the success of the transaction from our backend, every 5 second,
    // or until it polled for 10 times.
    const intervalHandler = setInterval(async () => {
      try {
        const response = await fetch(`/api/np-tokens/topup/${paymentIntentId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        if (!response.ok) {
          console.error('[NTTopupPaymentModal] HTTP Error:', response.status, response.statusText)
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data: {
          success: boolean
          data: {
            instance_id: string
            user_id: string
            stripe_payment_id: string
            amount: string
            token_amount: number
            package_id: string
            status: 'pending' | 'failed' | 'canceled' | 'completed'
            updated_at: string
          }
          error?: string
        } = await response.json()

        if (data.success) {
          // Handle cases server returns non error
          const paymentData = data.data
          if (paymentData.status === 'completed') {
            setPaymentStep('success')
            onPurchaseComplete(paymentData.token_amount || selectedPackage?.total_tokens || 0)
          } else if (paymentData.status === 'canceled' || paymentData.status === 'failed') {
            const errorMsg = `Payment ${paymentData.status}`
            console.error('[NTTopupPaymentModal] Payment not successful:', errorMsg)
            setError(errorMsg)
            setPaymentStep('failed')
          } else {
            // In cases where its still pending, go forward another turn.
            setPaymentResultPollCount((prev) => prev + 1)
            return
          }
        } else {
          // Case where server having some inherent error
          const errorMsg = data.error || 'Payment confirmation failed'
          console.error('[NTTopupPaymentModal] API error:', errorMsg)
          setError(errorMsg)
          setPaymentStep('failed')
        }
      } catch (err) {
        console.error('[NTTopupPaymentModal] Error confirming payment:', err)
        const errorMsg = err instanceof Error ? err.message : 'Network error confirming payment'
        setError(errorMsg)
        setPaymentStep('failed')
      }
    }, 5000)

    setPaymentResultPollIntervalHandler(intervalHandler)
  }

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage)
    setPaymentStep('failed')
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <DialogContent
        className={cn(
          'max-w-4xl max-h-[90vh] overflow-y-auto',
          'bg-neu-surface',
          'border-neu-border text-neu-text-primary',
          className
        )}
      >
        {/* Payment Form Loading Step (transition from NeuroToken Exchange) */}
        {paymentStep === 'payment_form' && selectedPackage && !clientSecret && (
          <div className="flex flex-col items-center justify-center py-14">
            <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
            <h3 className="text-lg font-semibold mb-1 text-neu-text-primary">
              Preparing payment...
            </h3>
            <p className="text-neu-text-muted text-center">Connecting Payment Gateway</p>
          </div>
        )}

        {/* Payment Form Step */}
        {paymentStep === 'payment_form' && selectedPackage && clientSecret && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Complete Payment
              </DialogTitle>
              <DialogDescription className="text-neu-text-muted">
                Secure payment powered by Stripe
              </DialogDescription>
            </DialogHeader>

            {error && (
              <Alert variant="destructive" className="bg-red-500/10 border-red-500/50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Balance + Purchase Summary in 2-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
              {/* Purchase Summary (left) */}
              <Card className="bg-neu-surface-recessed border border-neu-border-soft h-full order-2 md:order-1">
                <CardContent className="pt-6 space-y-3 text-center px-6 h-full flex flex-col items-center justify-center">
                  <div className="flex justify-center gap-4 text-neu-text-primary">
                    <span className="text-neu-text-muted">Package:</span>
                    <span className="font-medium">{selectedPackage.name}</span>
                  </div>
                  <div>
                    <img
                      src={getPackageIconPath(selectedPackage.name)}
                      alt={getPackageTierLabel(selectedPackage.name)}
                      className="mx-auto w-20 h-20 opacity-90 mb-1"
                    />
                    <div className="mt-1 text-3xl font-extrabold text-neu-text-primary">
                      {formatGameMoney(selectedPackage.total_tokens)} NT
                    </div>
                    {selectedPackage.bonus_tokens > 0 && (
                      <div className="text-neu-text-muted italic">
                        BONUS +{formatGameMoney(selectedPackage.bonus_tokens)}
                      </div>
                    )}
                  </div>
                  <div className="text-lg font-bold py-4 border-t border-neu-border-soft flex items-center justify-center gap-2 text-neu-text-primary">
                    <span>Total:</span>
                    <span>{selectedPackage.price_display}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Balance overview (right) */}
              <div className="bg-neu-surface-recessed border border-neu-border-soft rounded-lg p-4 text-center mb-0 h-full order-1 md:order-2 flex flex-col items-center justify-center">
                <div className="space-y-3 w-full">
                  <div>
                    <div className="text-xs text-neu-text-muted">Current Balance</div>
                    <div className="text-2xl font-semibold text-neu-text-primary">
                      {formatGameMoney(currentBalance)} NT
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neu-text-muted">Additional (from package)</div>
                    <div className="text-2xl font-semibold text-green-400">
                      + {formatGameMoney(selectedPackage.total_tokens)} NT
                    </div>
                    {selectedPackage.bonus_tokens > 0 && (
                      <div className="text-xs text-neu-text-muted mt-0.5">
                        Includes bonus +{formatGameMoney(selectedPackage.bonus_tokens)} NT
                      </div>
                    )}
                  </div>
                  <div className="border-t border-neu-border-soft pt-3">
                    <div className="text-xs text-neu-text-muted">Total after purchase</div>
                    <div className="text-3xl font-bold text-neu-text-primary">
                      {formatGameMoney(currentBalance + selectedPackage.total_tokens)} NT
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stripe Payment Form */}
            <div className="space-y-4">
              <StripeElementsWrapper
                clientSecret={clientSecret}
                amount={Math.round(selectedPackage.price_eur * 100)}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </div>
          </>
        )}

        {/* Processing Step */}
        {paymentStep === 'processing' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-16 h-16 animate-spin text-blue-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2 text-neu-text-primary">
              Processing Payment...
            </h3>
            <p className="text-neu-text-muted text-center">
              Please wait while we confirm your purchase
            </p>
          </div>
        )}

        {/* Success Step */}
        {paymentStep === 'success' && selectedPackage && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-neu-text-primary">
              Purchase Successful!
            </h3>
            <p className="text-neu-text-muted text-center mb-4">
              {formatGameMoney(selectedPackage.total_tokens)} NT tokens added to your balance
            </p>
            <div className="flex items-center gap-2 text-sm text-neu-text-muted">
              <Lock className="w-4 h-4" />
              <span>Secure transaction completed</span>
            </div>
          </div>
        )}

        {/* Failed Step */}
        {paymentStep === 'failed' && error && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <X className="w-10 h-10 text-red-500" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-neu-text-primary">Purchase Failed</h3>
            <p className="text-neu-text-muted text-center mb-2">
              Failed to topup NT tokens: {error}.
            </p>
            <p className="text-neu-text-muted text-center mb-4">Please try again later.</p>

            {/* {error && (
              <Alert variant="destructive" className="bg-red-500/10 border-red-500/50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )} */}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
